'use client'

import { cn } from '@/lib/utils'
import React from 'react'
import { AppTemplateCategory } from 'sprites-types/ui/templates'
import CategoryItem from './CategoryItem'
import SearchInput from './SearchInput'
import SortOptions from './SortOptions'

export type SortMode = 'Default' | 'Alphabetical' | 'Last Edited' | 'Created'
export type SortOrder = 'Ascending' | 'Descending'

interface AppSearchToolbarProps {
  activeCategory?: AppTemplateCategory['id']
  categories: AppTemplateCategory[]
  searchValue?: string
  onCategoryChange?: (categoryId: AppTemplateCategory['id']) => void
  onSearchChange?: (searchValue: string) => void

  availableSortModes: SortMode[]
  activeSortMode?: SortMode
  activeSortOrder?: SortOrder | null
  onSortModeChange?: (mode: SortMode) => void
  onSortOrderChange?: (order: SortOrder) => void
}

const AppSearchToolbar: React.FC<AppSearchToolbarProps> = ({
  activeCategory = 'All',
  categories,
  searchValue,
  onCategoryChange,
  onSearchChange,

  availableSortModes,
  activeSortMode,
  activeSortOrder,
  onSortModeChange,
  onSortOrderChange,
}) => {
  return (
    <nav
      aria-label="Template filters and search"
      className={cn('flex w-full flex-col gap-4 p-4 lg:flex-row lg:items-end lg:justify-start')}
    >
      {categories.length > 0 && (
        <div
          className={cn(
            "mb-2 flex w-full flex-wrap items-center gap-x-0.5 self-start rounded-2xl p-2 font-['Space_Grotesk'] text-sm lg:mb-0 lg:mr-4",
            'border border-black/10 bg-white/80 shadow-sm backdrop-blur-xl'
          )}
        >
          <ul className="flex w-full flex-wrap items-center gap-x-0.5" role="list">
            {categories.map((category) => (
              <li key={category.id}>
                <CategoryItem
                  className="h-[32px]"
                  category={category.name}
                  active={category.id === activeCategory}
                  onSelect={() => onCategoryChange?.(category.id)}
                />
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-start sm:justify-start">
        <SortOptions
          availableSortModes={availableSortModes}
          activeSortMode={activeSortMode}
          activeSortOrder={activeSortOrder}
          onSortModeChange={onSortModeChange}
          onSortOrderChange={onSortOrderChange}
          className="w-full sm:w-auto"
        />
        <label htmlFor="template-search" className="sr-only">
          Search templates
        </label>
        <SearchInput value={searchValue} onChange={onSearchChange} />
      </div>
    </nav>
  )
}

export default AppSearchToolbar
